import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../supabaseClient.jsx";
import { getBabaPhotoCandidates, handleBabaPhotoError } from "../../../utils/babaPhoto.js";
import { cn } from "../../../design-system/lib/cn.js";
import { Search, MapPin, Star, Zap, ChevronDown, X } from "lucide-react";

// ─── helpers ───────────────────────────────────────────────────────────────

const parseArea = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try {
    if (typeof val === "string" && val.trim().startsWith("[") ) return JSON.parse(val);
  } catch {}
  return typeof val === "string" && val.trim() ? [val] : [];
};

const NIVEL_LABEL = {
  "Iniciante (0-1 ano)": "Iniciante",
  "Intermediário (2-5 anos)": "Intermediário",
  "Experiente (5+ anos)": "Experiente",
};

const NIVEL_COLORS = {
  Iniciante: "bg-blue-50 text-blue-600",
  Intermediário: "bg-amber-50 text-amber-600",
  Experiente: "bg-emerald-50 text-emerald-600",
};

const AREAS_FILTRO = [
  "Recém-Nascidos",
  "Bebês (1-2 anos)",
  "Pré-escolar",
  "Idade Escolar",
  "Geral",
  "Autismo",
  "Necessidades Especiais",
];

// ─── BabaCard ───────────────────────────────────────────────────────────────

function BabaCard({ baba }) {
  const navigate = useNavigate();
  const areas = parseArea(baba.area_atuacao);
  const nivel = NIVEL_LABEL[baba.nivel_experiencia] ?? baba.nivel_experiencia;
  const nota = Number(baba.avaliacao_media) || 0;
  const totalReviews = Number(baba.total_avaliacoes) || 0;

  return (
    <article className="group flex flex-col bg-white rounded-2xl border border-warm-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 overflow-hidden">
      {/* Foto */}
      <div className="relative aspect-4/3 overflow-hidden bg-warm-100">
        <img
          src={baba.displayFotoUrl}
          alt={`${baba.nome} ${baba.sobrenome}`}
          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => handleBabaPhotoError(e, baba.fotoCandidates)}
        />
        {baba.aceita_emergencial && (
          <span className="absolute top-2.5 left-2.5 flex items-center gap-1 rounded-full bg-rose-400 px-2.5 py-1 text-[10px] font-bold text-white shadow">
            <Zap size={10} fill="currentColor" /> Emergencial
          </span>
        )}
        {nivel && (
          <span
            className={cn(
              "absolute top-2.5 right-2.5 rounded-full px-2.5 py-1 text-[10px] font-bold shadow",
              NIVEL_COLORS[nivel] ?? "bg-white text-warm-700"
            )}
          >
            {nivel}
          </span>
        )}
      </div>

      {/* Conteúdo */}
      <div className="flex flex-col flex-1 p-4 gap-3">
        {/* Nome + cidade */}
        <div>
          <h3 className="font-bold text-warm-900 text-base leading-tight">
            {baba.nome} {baba.sobrenome}
          </h3>
          {baba.cidade && (
            <p className="flex items-center gap-1 text-xs text-warm-400 mt-1">
              <MapPin size={11} /> {baba.cidade}
            </p>
          )}
        </div>

        {/* Avaliação */}
        <div className="flex items-center gap-1.5">
          {nota > 0 ? (
            <>
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={13} className={
                  s <= Math.round(nota)
                    ? "fill-amber-400 text-amber-400"
                    : "fill-warm-200 text-warm-200"
                } />
              ))}
              <span className="text-xs font-semibold text-warm-800 ml-0.5">
                {nota.toFixed(1)}
              </span>
              <span className="text-xs text-warm-400">({totalReviews})</span>
            </>
          ) : (
            <span className="text-xs text-warm-400">
              Seja a primeira a avaliar
            </span>
          )}
        </div>

        {/* Bio */}
        {baba.bio && (
          <p className="text-xs text-warm-600 leading-relaxed line-clamp-3">
            {baba.bio}
          </p>
        )}

        {/* Áreas */}
        {areas.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-auto">
            {areas.slice(0, 3).map((a) => (
              <span key={a} className="rounded-lg bg-rose-400/10 text-rose-400 px-2.5 py-0.5 text-[10px] font-semibold">
                {a}
              </span>
            ))}
            {areas.length > 3 && (
              <span className="rounded-lg bg-warm-100 text-warm-500 px-2.5 py-0.5 text-[10px] font-semibold">
                +{areas.length - 3}
              </span>
            )}
          </div>
        )}

        {/* CTA */}
        <button
          onClick={() => navigate(`/perfil-baba/${baba.id}`)}
          className="mt-auto w-full rounded-xl bg-rose-400 py-2.5 text-sm font-semibold text-white hover:brightness-110 active:scale-95 transition-all"
        >
          Ver Perfil
        </button>
      </div>
    </article>
  );
}

// ─── FilterPill ─────────────────────────────────────────────────────────────

function FilterPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-full border px-4 py-1.5 text-xs font-semibold transition-all",
        active
          ? "border-rose-400 bg-rose-400 text-white"
          : "border-warm-200 bg-white text-warm-600 hover:border-rose-400 hover:text-rose-400"
      )}
    >
      {label}
    </button>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function TodasAsBabasPage() {
  const [babas, setBabas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [areasFiltro, setAreasFiltro] = useState([]);
  const [somenteEmergencial, setSomenteEmergencial] = useState(false);
  const [nivelFiltro, setNivelFiltro] = useState("");
  const [cidadeFiltro, setCidadeFiltro] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("view_babas_publicas")
        .select("*")
        .order("avaliacao_media", { ascending: false, nullsLast: true })
        .order("total_avaliacoes", { ascending: false })
        .order("created_at", { ascending: false });

      if (!error && data) {
        setBabas(
          data.map((b) => {
            const fotoCandidates = getBabaPhotoCandidates(b.foto_url);
            return { ...b, fotoCandidates, displayFotoUrl: fotoCandidates[0] };
          })
        );
      }
      setLoading(false);
    })();
  }, []);

  const cidades = useMemo(() => {
    const set = new Set(babas.map((b) => b.cidade).filter(Boolean));
    return [...set].sort();
  }, [babas]);

  const filtradas = useMemo(() => {
    const term = search.toLowerCase();
    return babas.filter((b) => {
      const nomeMatch = `${b.nome} ${b.sobrenome}`.toLowerCase().includes(term);
      const areas = parseArea(b.area_atuacao);
      const areaMatch = areasFiltro.length === 0 || areasFiltro.some((a) => areas.includes(a));
      const emergencialMatch = !somenteEmergencial || b.aceita_emergencial;
      const nivelMatch = !nivelFiltro || (b.nivel_experiencia || "").includes(nivelFiltro);
      const cidadeMatch = !cidadeFiltro || b.cidade === cidadeFiltro;
      return nomeMatch && areaMatch && emergencialMatch && nivelMatch && cidadeMatch;
    });
  }, [babas, search, areasFiltro, somenteEmergencial, nivelFiltro, cidadeFiltro]);

  const toggleArea = (area) =>
    setAreasFiltro((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    );

  const hasFilters =
    search || areasFiltro.length || somenteEmergencial || nivelFiltro || cidadeFiltro;

  const limparFiltros = () => {
    setSearch("");
    setAreasFiltro([]);
    setSomenteEmergencial(false);
    setNivelFiltro("");
    setCidadeFiltro("");
  };

  return (
    <div className="min-h-screen bg-warm-50">
      {/* Hero */}
      <section className="bg-linear-to-br from-slate-blue-400 to-[#2f3370] pt-28 pb-14 px-4 text-center text-white">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
          Nossas Babás
        </h1>
        <p className="text-white/75 text-sm sm:text-base max-w-md mx-auto">
          Profissionais verificadas, avaliadas e prontas para cuidar do seu
          tesourinho com amor e dedicação.
        </p>
        <div className="mx-auto mt-8 max-w-lg">
          <div className="relative">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-warm-400" />
            <input
              type="text"
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-full border-0 bg-white py-3 pl-11 pr-4 text-sm text-warm-800 placeholder:text-warm-400 shadow-lg focus:outline-none focus:ring-2 focus:ring-white/50"
            />
          </div>
        </div>
      </section>

      {/* Filtros */}
      <section className="sticky top-16 z-30 bg-white border-b border-warm-100 shadow-sm">
        <div className="mx-auto max-w-6xl px-4 py-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-warm-500 mr-1 shrink-0">
            Filtrar:
          </span>

          {AREAS_FILTRO.map((area) => (
            <FilterPill key={area} label={area} active={areasFiltro.includes(area)} onClick={() => toggleArea(area)} />
          ))}

          <FilterPill label="⚡ Emergencial" active={somenteEmergencial} onClick={() => setSomenteEmergencial((v) => !v)} />

          {/* Nível select */}
          <div className="relative">
            <select value={nivelFiltro} onChange={(e) => setNivelFiltro(e.target.value)}
              className={cn(
                "appearance-none rounded-full border px-4 py-1.5 pr-7 text-xs font-semibold transition-all cursor-pointer focus:outline-none",
                nivelFiltro ? "border-rose-400 bg-rose-400 text-white" : "border-warm-200 bg-white text-warm-600"
              )}
            >
              <option value="">Experiência</option>
              <option value="Iniciante">Iniciante</option>
              <option value="Intermediário">Intermediário</option>
              <option value="Experiente">Experiente</option>
            </select>
            <ChevronDown size={11} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-current opacity-60" />
          </div>

          {/* Cidade select */}
          {cidades.length > 0 && (
            <div className="relative">
              <select value={cidadeFiltro} onChange={(e) => setCidadeFiltro(e.target.value)}
                className={cn(
                  "appearance-none rounded-full border px-4 py-1.5 pr-7 text-xs font-semibold transition-all cursor-pointer focus:outline-none",
                  cidadeFiltro ? "border-rose-400 bg-rose-400 text-white" : "border-warm-200 bg-white text-warm-600"
                )}
              >
                <option value="">Cidade</option>
                {cidades.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <ChevronDown size={11} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-current opacity-60" />
            </div>
          )}

          {hasFilters && (
            <button onClick={limparFiltros} className="ml-auto flex items-center gap-1 text-xs text-warm-400 hover:text-rose-400 transition-colors">
              <X size={13} /> Limpar
            </button>
          )}
        </div>
      </section>

      {/* Grid */}
      <main className="mx-auto max-w-6xl px-4 py-10">
        {!loading && (
          <p className="text-sm text-warm-400 mb-6">
            {filtradas.length === 0 ? "Nenhuma babá encontrada com esses filtros." : `${filtradas.length} babá${filtradas.length !== 1 ? "s" : ""} encontrada${filtradas.length !== 1 ? "s" : ""}`}
          </p>
        )}

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-white border border-warm-100 overflow-hidden animate-pulse">
                <div className="aspect-4/3 bg-warm-100" />
                <div className="p-4 space-y-2">
                  <div className="h-4 w-3/4 bg-warm-100 rounded" />
                  <div className="h-3 w-1/2 bg-warm-100 rounded" />
                  <div className="h-3 w-full bg-warm-100 rounded" />
                  <div className="h-3 w-5/6 bg-warm-100 rounded" />
                  <div className="h-9 w-full bg-warm-100 rounded-xl mt-4" />
                </div>
              </div>
            ))}
          </div>
        ) : filtradas.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-4xl mb-4">🔍</p>
            <p className="text-warm-500 text-sm">
              Nenhuma babá encontrada. <button onClick={limparFiltros} className="text-rose-400 font-semibold underline">Limpar filtros</button>
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtradas.map((baba) => (
              <BabaCard key={baba.id} baba={baba} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
